import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Coffee, ShoppingBag, Monitor, Factory, Leaf, Briefcase } from "lucide-react";
import { api, media } from "@/lib/api";
import { useMarket } from "@/lib/market";
import { THEMES, CAT_THEME, VERIF_LABEL } from "@/lib/fmt";
import ListingCard from "@/components/ListingCard";
import { VerifPill, Loading } from "@/components/Bits";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ICONS = { cafe: Coffee, retail: ShoppingBag, digital: Monitor, manufacturing: Factory, wellness: Leaf, services: Briefcase };
const FAQ = [
  ["Are the figures on listings checked?", "Figures are supplied by sellers. Each listing shows whether numbers are actual results, estimates or forecasts, and separate verification statuses explain exactly what — if anything — a reviewer compared."],
  ["Does VyapaarSetu handle the purchase money?", "No. We do not process acquisition funds or provide escrow. Buyers and sellers complete payment through their own advisers and banks."],
  ["What does an NDA unlock?", "Accepting an NDA records your agreement. It does not unlock documents on its own — the seller or an admin must approve document access, and can revoke it."],
  ["Is a verification badge a guarantee?", "No. A badge describes the scope of a recorded review. It never promises future profit, clean title or a successful acquisition."],
  ["Can I be both a buyer and a seller?", "Yes. One account, with a role switcher in your dashboard."],
];

function Hero({ imgId }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  return (
    <section className="relative overflow-hidden atmosphere" style={{ "--atm-bg": "#EFE2D0" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 fade-up">
          <p className="eyebrow mb-5">India's marketplace for operating businesses</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#29483D] leading-[1.05]">Someone built it.<br /><span className="italic text-[#B86545]">You can take it forward.</span></h1>
          <p className="mt-6 text-base sm:text-lg text-[#49392F] max-w-xl">Discover operating businesses. Understand the numbers. Buy with clarity.</p>
          <form onSubmit={(e) => { e.preventDefault(); nav(`/browse?q=${encodeURIComponent(q)}`); }} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-xl" role="search">
            <label htmlFor="hero-search" className="sr-only">Search businesses</label>
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input id="hero-search" className="inp !min-h-[52px] !rounded-full pl-10" placeholder="Café, agency, Pune, online…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="hero-search-input" />
            </div>
            <button className="btn-primary !min-h-[52px]" data-testid="hero-search-btn">Search</button>
          </form>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/browse" className="btn-accent" data-testid="explore-businesses-btn">Explore Businesses <ArrowRight size={16} /></Link>
            <Link to="/signup?role=seller" className="btn-ghost" data-testid="sell-your-business-btn">Sell Your Business</Link>
          </div>
        </div>
        <div className="lg:col-span-5 relative hidden sm:block">
          <div className="rounded-[2rem] p-3 bg-[#E9DDCB] rotate-1 shadow-[var(--shadow)]">
            <div className="rounded-[1.5rem] overflow-hidden aspect-[4/5] bg-[#D9C7AE]">
              {imgId && <img src={media(imgId, "public")} alt="Demo listing photo (licensed placeholder)" className="w-full h-full object-cover" />}
            </div>
          </div>
          <div className="absolute -bottom-6 -left-6 card p-4 max-w-[240px] bg-[#F7F3EB]">
            <p className="text-xs text-stone-500">Every figure is labelled</p>
            <p className="font-display text-[#29483D] mt-1">Actual · Estimate · Forecast</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { meta, savedIds, toggleSave, compare, toggleCompare, catName } = useMarket();
  const [featured, setFeatured] = useState(null);
  const [recent, setRecent] = useState(null);
  useEffect(() => {
    api.get("/public/listings", { params: { featured: true, page_size: 3 } }).then((r) => setFeatured(r.data.items)).catch(() => setFeatured([]));
    api.get("/public/listings", { params: { page_size: 6 } }).then((r) => setRecent(r.data.items)).catch(() => setRecent([]));
  }, []);
  const card = (l) => <ListingCard key={l.id} l={l} saved={savedIds.includes(l.id)} onSave={toggleSave} inCompare={compare.includes(l.id)} onCompare={toggleCompare} catName={catName(l.data.category)} />;
  return (
    <>
      <Hero imgId={featured?.[0]?.cover_image_id} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {meta.categories.map((c) => {
            const t = THEMES[CAT_THEME[c.slug] || "services"]; const Icon = ICONS[c.theme] || Briefcase;
            return (
              <Link key={c.slug} to={`/category/${c.slug}`} className="card p-4 hover:-translate-y-0.5 transition-transform" style={{ background: t.bg }} data-testid={`category-card-${c.slug}`}>
                <Icon size={22} style={{ color: t.accent }} />
                <p className="font-semibold text-sm mt-3" style={{ color: t.ink }}>{c.name}</p>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
        <div className="flex items-end justify-between mb-6"><div><p className="eyebrow">Featured</p><h2 className="text-2xl sm:text-3xl text-[#29483D] mt-1">Featured listings</h2><p className="text-xs text-stone-500 mt-1">Featured placement is paid promotion and is separate from verification.</p></div><Link to="/browse" className="text-sm font-semibold text-[#B86545]">View all</Link></div>
        {featured === null ? <Loading /> : featured.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{featured.map(card)}</div> : <p className="text-sm text-stone-500">No featured listings right now.</p>}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
        <div className="flex items-end justify-between mb-6"><div><p className="eyebrow">Fresh</p><h2 className="text-2xl sm:text-3xl text-[#29483D] mt-1">Recently added</h2></div></div>
        {recent === null ? <Loading /> : recent.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{recent.map(card)}</div> : <p className="text-sm text-stone-500">No listings published yet.</p>}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5"><p className="eyebrow">Verification with meaning</p><h2 className="text-2xl sm:text-3xl text-[#29483D] mt-2">Seven separate checks. Plain-language labels.</h2>
          <p className="text-sm text-stone-600 mt-3">Identity, authority to sell, business existence, financial documents, premises, licences and images are tracked separately. Uploading a document never earns a badge on its own.</p>
          <Link to="/page/verification" className="btn-ghost mt-5">How verification works</Link></div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-2">
          {Object.keys(VERIF_LABEL).map((s) => <div key={s} className="card p-3 flex items-center"><VerifPill status={s} /></div>)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24 grid md:grid-cols-2 gap-6">
        {[["For buyers", ["Search by category, city and the numbers that matter", "Save, compare up to three and set alerts", "Enquire privately, accept the NDA, request documents", "Make offers and track every stage to handover"], "/browse", "Explore Businesses", "#E2ECE9"],
          ["For sellers", ["Guided 10-step listing that autosaves", "Private document room — you approve every buyer", "Screen buyers, respond to offers, plan the handover", "Fees only through clear, published rules"], "/signup?role=seller", "Sell Your Business", "#F6EBDD"]].map(([t, steps, to, cta, bg]) => (
          <div key={t} className="card p-7" style={{ background: bg }}>
            <h3 className="text-2xl text-[#29483D]">{t}</h3>
            <ol className="mt-4 space-y-3">{steps.map((s, i) => <li key={s} className="flex gap-3 text-sm"><span className="w-6 h-6 rounded-full bg-[#29483D] text-white text-xs flex items-center justify-center shrink-0">{i + 1}</span>{s}</li>)}</ol>
            <Link to={to} className="btn-primary mt-6">{cta}</Link>
          </div>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-24">
        <p className="eyebrow text-center">Questions</p><h2 className="text-2xl sm:text-3xl text-[#29483D] text-center mt-2 mb-6">Frequently asked</h2>
        <Accordion type="single" collapsible className="card px-5">
          {FAQ.map(([q, a], i) => <AccordionItem key={q} value={`f${i}`}><AccordionTrigger className="text-left font-semibold" data-testid={`faq-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-stone-600">{a}</AccordionContent></AccordionItem>)}
        </Accordion>
      </section>
    </>
  );
}
